/**
 * Back-in-Stock Notification — Layer E
 * File: assets/back-in-stock.js
 *
 * Depends on: lead-capture-utils.js (LiftSetsCapture global)
 * 
 * Behaviour:
 * - Detects sold out state via the Add to Cart button (disabled + text)
 * - Uses MutationObserver to watch for variant changes in real-time
 * - Shows "Notify Me" form when variant is unavailable
 * - Hides when variant is available
 * - Tags customer with: back-in-stock, product handle, variant info
 * - Validates email + blocks disposables
 * - Lets Shopify form submit naturally (CAPTCHA safe)
 * - Subscribes to Klaviyo Back in Stock API (auto-notifies on restock)
 */

(function () {
  'use strict';

  var LC = window.LiftSetsCapture;
  if (!LC) return;

  /* ============================================================
     KLAVIYO CONFIG
     Replace with your Klaviyo public API key (site ID)
     Find in: Klaviyo → Settings → API Keys → Public API Key
     ============================================================ */

  var KLAVIYO_PUBLIC_KEY = 'SrEjEd';  // e.g. 'abc123'
  var KLAVIYO_API_REVISION = '2024-10-15';

  /* ============================================================
     DOM REFERENCES
     ============================================================ */

  var capture    = document.getElementById('ls-bis-capture');
  if (!capture) return;

  var form       = document.getElementById('ls-bis-form');
  var emailInput = document.getElementById('ls-bis-email');
  var tagsInput  = document.getElementById('ls-bis-tags');
  var errorEl    = document.getElementById('ls-bis-error');
  var successEl  = document.getElementById('ls-bis-success');
  var innerEl    = capture.querySelector('.ls-bis-inner');

  var productHandle = capture.getAttribute('data-product-handle') || '';
  var productTitle  = capture.getAttribute('data-product-title') || '';

  /* ============================================================
     CHECK IF ALREADY SUBMITTED FOR THIS PRODUCT
     ============================================================ */

  var bisKey = 'liftsets_bis_' + productHandle;

  if (LC.getLocal(bisKey)) {
    capture.style.display = '';
    innerEl.style.display = 'none';
    successEl.style.display = 'flex';
    // Don't return — still need to show/hide based on variant
  }

  /* ============================================================
     FIND THE ADD TO CART BUTTON
     
     This theme uses <product-form-component> with content_for blocks.
     The button could match several selectors depending on the theme.
     ============================================================ */

  function findAddButton() {
    // Try multiple selectors used by various Shopify themes
    return document.querySelector(
      '.add-to-cart-button, ' +
      'button[name="add"], ' +
      '.product-form__submit, ' +
      '.product-form-buttons button[type="submit"], ' +
      'product-form-component button[type="submit"]'
    );
  }

  /* ============================================================
     CHECK SOLD OUT STATE
     ============================================================ */

  function isSoldOut() {
    var btn = findAddButton();
    if (!btn) return false;

    // Check disabled attribute
    if (btn.disabled) return true;

    // Check button text
    var text = (btn.textContent || '').trim().toLowerCase();
    if (text.indexOf('sold out') !== -1) return true;
    if (text.indexOf('unavailable') !== -1) return true;

    return false;
  }

  function updateVisibility() {
    var alreadySignedUp = LC.getLocal(bisKey);

    if (isSoldOut()) {
      capture.style.display = '';
      if (alreadySignedUp) {
        innerEl.style.display = 'none';
        successEl.style.display = 'flex';
      }
    } else {
      capture.style.display = 'none';
    }
  }

  function updateTags() {
    // Try to get current variant title from the page
    var variantTitle = '';

    // Method 1: Check selected option values
    var selectedOptions = document.querySelectorAll('.product-option input:checked, .product-option select');
    var optionValues = [];
    for (var i = 0; i < selectedOptions.length; i++) {
      var val = selectedOptions[i].value || '';
      if (val) optionValues.push(val);
    }
    if (optionValues.length) {
      variantTitle = optionValues.join('-');
    }

    // Build tags
    var tags = ['back-in-stock', 'bis-' + productHandle];
    if (variantTitle) {
      tags.push('bis-variant-' + slugify(variantTitle));
    }
    tagsInput.value = tags.join(', ');
  }

  function slugify(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  /* ============================================================
     GET CURRENT VARIANT ID
     
     Tries multiple methods to find the currently selected
     Shopify variant ID, needed for Klaviyo BIS subscription.
     ============================================================ */

  function getCurrentVariantId() {
    // Method 1: Hidden input in the product form (most Shopify themes)
    var variantInput = document.querySelector(
      'input[name="id"][type="hidden"], ' +
      'select[name="id"], ' +
      'input[name="id"]'
    );
    if (variantInput && variantInput.value) return variantInput.value;

    // Method 2: URL parameter
    var urlParams = new URLSearchParams(window.location.search);
    var urlVariant = urlParams.get('variant');
    if (urlVariant) return urlVariant;

    // Method 3: data attribute on the capture element
    var variantAttr = capture.getAttribute('data-variant-id');
    if (variantAttr) return variantAttr;

    return null;
  }

  /* ============================================================
     KLAVIYO BACK IN STOCK SUBSCRIPTION
     
     Uses the client-side (public) endpoint — no private key needed.
     Creates a "Subscribed to Back in Stock" event on the profile.
     Klaviyo then watches inventory and auto-sends when restocked.
     
     Docs: https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
     ============================================================ */

  function submitToKlaviyo(email, variantId) {
    if (!variantId || KLAVIYO_PUBLIC_KEY === 'YOUR_KLAVIYO_PUBLIC_API_KEY') {
      // Skip if no variant ID found or key not configured yet
      return;
    }

    var url = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + KLAVIYO_PUBLIC_KEY;

    var payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email
              }
            }
          }
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              // Shopify variant ID format for Klaviyo
              id: '$shopify:::$default:::' + variantId
            }
          }
        }
      }
    };

    // Fire and forget — don't block the Shopify form submission
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': KLAVIYO_API_REVISION
      },
      body: JSON.stringify(payload)
    }).catch(function(err) {
      // Log but don't block — Shopify tags are the fallback
      console.warn('[LiftSets BIS] Klaviyo API error:', err.message);
    });
  }

  /* ============================================================
     MUTATION OBSERVER
     
     Watches the Add to Cart button for changes to:
     - disabled attribute (variant availability)
     - text content (Sold Out / Add to Cart)
     
     Also watches the parent form for child changes
     (some themes replace the entire button element).
     ============================================================ */

  function startObserver() {
    var btn = findAddButton();
    if (!btn) return;

    // Watch the button itself
    var btnObserver = new MutationObserver(function () {
      updateVisibility();
      updateTags();
    });

    btnObserver.observe(btn, {
      attributes: true,
      attributeFilter: ['disabled', 'class'],
      childList: true,
      characterData: true,
      subtree: true
    });

    // Also watch the form container for structural changes
    var formContainer = btn.closest('.product-form-buttons') || btn.closest('product-form-component');
    if (formContainer && formContainer !== btn) {
      var containerObserver = new MutationObserver(function () {
        // Button might have been replaced — find it again
        updateVisibility();
        updateTags();
      });

      containerObserver.observe(formContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'class']
      });
    }
  }

  /* ============================================================
     ALSO LISTEN FOR CHANGE EVENTS (backup detection)
     ============================================================ */

  // Listen for any input/select changes in the product form area
  var productSection = capture.closest('.product-information') || capture.closest('[data-product-grid-content]') || document.body;

  productSection.addEventListener('change', function (e) {
    // Small delay to let the theme update the button state
    setTimeout(function () {
      updateVisibility();
      updateTags();
    }, 150);
  });

  // Listen for custom variant change events (some themes fire these)
  document.addEventListener('variant:change', function () {
    setTimeout(function () {
      updateVisibility();
      updateTags();
    }, 150);
  });

  /* ============================================================
     ERROR HANDLING
     ============================================================ */

  function showError(msg) {
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
    emailInput.classList.add('ls-input-error');
  }

  function clearError() {
    errorEl.style.display = 'none';
    errorEl.textContent = '';
    emailInput.classList.remove('ls-input-error');
  }

  /* ============================================================
     FORM SUBMIT HANDLER
     ============================================================ */

  async function handleSubmit(e) {
    clearError();

    var email = emailInput.value.trim().toLowerCase();

    if (!LC.isValidEmail(email)) {
      e.preventDefault();
      showError('Please enter a valid email address.');
      return;
    }

    await LC.loadDisposableDomains();
    if (LC.isDisposableEmail(email)) {
      e.preventDefault();
      showError('Please use a non-temporary email address.');
      return;
    }

    // Update tags before submit
    updateTags();

    // -------------------------------------------------------
    // Klaviyo: Subscribe to Back in Stock (fire and forget)
    // This runs in parallel with the Shopify form submission.
    // If it fails, Shopify tags still work as a fallback.
    // -------------------------------------------------------
    var variantId = getCurrentVariantId();
    submitToKlaviyo(email, variantId);

    // Save state
    LC.setLocal(bisKey, '1');
    LC.markSubmitted();
    LC.fireEvent('bis_email_submitted', {
      popup_type: 'back-in-stock',
      product_handle: productHandle,
      product_title: productTitle,
      variant_id: variantId || ''
    });
    LC.fireEnhancedConversion(email);

    // Let Shopify form submit naturally (CAPTCHA safe)
  }

  /* ============================================================
     EVENT LISTENERS
     ============================================================ */

  form.addEventListener('submit', handleSubmit);

  emailInput.addEventListener('input', function () {
    if (errorEl.style.display !== 'none') clearError();
  });

  /* ============================================================
     INIT
     ============================================================ */

  // Delay init to let product page scripts load first
  setTimeout(function () {
    updateVisibility();
    updateTags();
    startObserver();
  }, 500);

})();